import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'
import { browserName } from 'lib/bowser'

import isInExperiment from 'lib/isInExperiment'
import RegistrySideNavContainer from 'registry/components/registry-sidenav'
import RegistrySetupContainer from 'registry/components/registry-setup'
import { InfoButton } from 'components/buttons'
import { DismissableAppPromoBanner } from 'components/banners/app-promo-banner'
import Link from 'components/link'
import {
  HOME,
  ADD_BABYLIST_BUTTON,
  INSTALL_BABYLIST_EXTENSION,
  RIGHT_NAV_ADD_ITEM_SCREEN_VIEW_EVENT,
} from 'registry/constants'
import {
  setActiveModal,
  fetchCategorizedRegItems,
  fetchLatestFeedSession,
} from 'registry/actions'
import {
  getIsFetchingFeed,
  getIsFetchingCategorizedRegItems,
  getBabylistExtensionInstalled,
  getRegistryOwnerId,
  getRegistryId,
} from 'registry/reducers'
import * as BLUrls from 'lib/urls'
import { track, TrackImpression } from 'lib/analytics'
import { useDisplayBirthdayWishlistModal } from 'shared/BirthdayWishlistModal/BirthdayWishlistModal.utils'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import FeedList from './components/feed-list'
import FeedAdminTool from './components/FeedAdminTools/FeedAdminTools'
import MessageCenterAdminTools from './components/MessageCenterAdminTools/MessageCenterAdminTools'
import css from './registry-home.scss'
import classNames from 'classnames'
import Plus from 'baby-design/icons/Plus.svg'

const RegistryHome = ({
  fetchLatestFeedSession,
  isAdmin,
  isFetchingCategorizedRegItems,
  isFetchingFeed,
  setActiveModal,
  showAppPromoBanner,
  installedBabylistExtension,
  isRouterMounted,
  registryOwnerId,
  registryId,
}) => {
  const [currentUser] = useCurrentUser()

  useDisplayBirthdayWishlistModal(
    { id: registryId, ownerId: registryOwnerId },
    track.EventLocation.LIST_ADOPTION_MODAL_MINUS_FEED
  )

  useEffect(() => {
    fetchCategorizedRegItems()
  }, [isFetchingCategorizedRegItems])

  let shouldShowInstallBabylistButton = !currentUser?.addedBabylistButton
  let handleInstallBabylistButton = () => setActiveModal(ADD_BABYLIST_BUTTON)

  const isInDayZeroDesktopExperiment =
    browserName === 'Chrome' &&
    useFeatureFlag('24bExDayzeroDesktopExtensionV2', 'control').flagValue ===
      'variation1'
  if (
    isInDayZeroDesktopExperiment ||
    isInExperiment(currentUser?.experiments, 'act_ex9')
  ) {
    shouldShowInstallBabylistButton = !installedBabylistExtension
    handleInstallBabylistButton = () =>
      setActiveModal(INSTALL_BABYLIST_EXTENSION)
  }

  const isInWebAddItemsExperiment =
    useFeatureFlag('24b_ex_dayzero_web_add_items', 'control').flagValue ===
    'variation1'

  const handleAddItemsButton = () => {
    window.open(
      `${BLUrls.registryAddItemsPath}?entry_point=${RIGHT_NAV_ADD_ITEM_SCREEN_VIEW_EVENT}`
    )
  }

  const getButtonPath = isInDayZeroDesktopExperiment
    ? BLUrls.addButtonPath
    : BLUrls.addItemsPath

  return (
    <TrackImpression
      eventProperties={{ event: track.registryHomeViewed }}
      shouldFireEvent={isRouterMounted}
    >
      {showAppPromoBanner && <DismissableAppPromoBanner />}
      <div className="container mts mtl-sm pbxl">
        <Row>
          <Col className="hidden-xs" md={3} sm={3} xl={2}>
            <RegistrySideNavContainer activeNav={HOME} />

            {isAdmin && (
              <>
                <FeedAdminTool
                  isFetchingFeed={isFetchingFeed}
                  onSubmit={fetchLatestFeedSession}
                />
                <MessageCenterAdminTools />
              </>
            )}
          </Col>

          <Col md={9} sm={9} xl={10}>
            <RegistrySetupContainer showHeader className="mtxl" />

            <Row className="mtl">
              <Col className="prl-md" lg={9} sm={8}>
                <FeedList isAdmin={isAdmin} />
              </Col>
              <Col className="hidden-xs" lg={3} sm={4}>
                <section aria-label="Useful Links">
                  {!isInWebAddItemsExperiment && (
                    <div className="phl ptxl">
                      {shouldShowInstallBabylistButton && (
                        <InfoButton
                          block
                          empty
                          pill
                          className="mbm"
                          size="small"
                          onClick={handleInstallBabylistButton}
                        >
                          Install Babylist Button
                        </InfoButton>
                      )}
                      <InfoButton
                        block
                        empty
                        newWindow
                        pill
                        className="mbm phs"
                        href={BLUrls.helpAndFavorsPath}
                        size="small"
                      >
                        Add Help &amp; Favors
                      </InfoButton>
                      <InfoButton
                        block
                        empty
                        pill
                        className="mbm phs"
                        size="small"
                        onClick={() => window.open(BLUrls.registryExternalPath)}
                      >
                        Transfer Registry
                      </InfoButton>
                      <InfoButton
                        block
                        empty
                        newWindow
                        pill
                        className="mbm phs"
                        href={BLUrls.cashFundsPath}
                        size="small"
                      >
                        Add a Cash Fund
                      </InfoButton>
                    </div>
                  )}

                  {isInWebAddItemsExperiment && (
                    <div className={css.usefulLinks__pillButtonContainer}>
                      <InfoButton
                        block
                        empty
                        pill
                        className={classNames(
                          css['usefulLinks__pillButton--filled'],
                          css['usefulLinks__pillButton--iconContainer']
                        )}
                        size="small"
                        onClick={handleAddItemsButton}
                      >
                        <Plus /> Add items
                      </InfoButton>
                      <InfoButton
                        block
                        empty
                        newWindow
                        pill
                        className={css['usefulLinks__pillButton--empty']}
                        href={BLUrls.helpAndFavorsPath}
                        size="small"
                      >
                        Add Help &amp; Favors
                      </InfoButton>
                      <InfoButton
                        block
                        empty
                        newWindow
                        pill
                        className={css['usefulLinks__pillButton--empty']}
                        href={BLUrls.cashFundsPath}
                        size="small"
                      >
                        Add a Cash Fund
                      </InfoButton>
                      <InfoButton
                        block
                        empty
                        pill
                        className={css['usefulLinks__pillButton--empty']}
                        size="small"
                        onClick={() => window.open(BLUrls.registryExternalPath)}
                      >
                        Transfer Registry
                      </InfoButton>
                    </div>
                  )}

                  <hr className="mvxl" />

                  <div className="phl">
                    <div className="h6 text-uppercase text-bold text-light tiny">
                      Useful Links
                    </div>
                    <ul className="list-unstyled">
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={getButtonPath}
                        >
                          Get the Babylist Button
                        </Link>
                      </li>
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={BLUrls.faqPath}
                        >
                          Registry FAQs
                        </Link>
                      </li>
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={BLUrls.couponsPath}
                        >
                          Coupons & Promotions
                        </Link>
                      </li>
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={BLUrls.storeCategoryPath('exclusives')}
                        >
                          Babylist Exclusives
                        </Link>
                      </li>
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={BLUrls.babylistHealth}
                        >
                          Get a Free Breast Pump
                        </Link>
                      </li>
                      <li className="mbm">
                        <Link
                          className="text-secondary text-bold h7"
                          url={BLUrls.findPath}
                        >
                          Find a Registry
                        </Link>
                      </li>
                    </ul>

                    <hr className="mvxl" />

                    <p className="h7">
                      Just a little reminder
                      <br />
                      <strong>You got this! 💪</strong>
                    </p>
                  </div>
                </section>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </TrackImpression>
  )
}

RegistryHome.propTypes = {
  fetchLatestFeedSession: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isFetchingCategorizedRegItems: PropTypes.bool,
  isFetchingFeed: PropTypes.bool.isRequired,
  isRouterMounted: PropTypes.bool,
  setActiveModal: PropTypes.func.isRequired,
  showAppPromoBanner: PropTypes.bool.isRequired,
  installedBabylistExtension: PropTypes.bool.isRequired,
  registryOwnerId: PropTypes.number.isRequired,
  registryId: PropTypes.number.isRequired,
}

RegistryHome.defaultProps = {
  isFetchingCategorizedRegItems: false,
  isRouterMounted: false,
}

RegistryHome.contextTypes = {
  isAdmin: PropTypes.bool,
  registry: PropTypes.object,
}

const mapStateToProps = (state) => ({
  isFetchingFeed: getIsFetchingFeed(state),
  isFetchingCategorizedRegItems: getIsFetchingCategorizedRegItems(state),
  installedBabylistExtension: !!getBabylistExtensionInstalled(state),
  registryOwnerId: getRegistryOwnerId(state),
  registryId: getRegistryId(state),
  registryType: state.registry?.type,
})

export default connect(mapStateToProps, {
  setActiveModal,
  fetchCategorizedRegItems,
  fetchLatestFeedSession,
})(RegistryHome)
